import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/components/AuthProvider";
import { ROUTE_PATHS } from "@/routes/routePaths";
import { clearAuthSession } from "@/utils/authStorage";
import {
  onSessionExpired,
  resetSessionExpiredState,
} from "@/utils/sessionExpired";

/**
 * Global modal shown when token refresh fails (e.g. refresh API returns 401).
 * Clears persisted auth on confirm and returns the user to the public home page.
 */
export function SessionExpiredDialog() {
  const { setAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => onSessionExpired(() => setOpen(true)), []);

  const handleOkay = () => {
    clearAuthSession();
    setAuthenticated(false);
    resetSessionExpiredState();
    setOpen(false);
    window.location.href = ROUTE_PATHS.HOME;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && open) handleOkay();
      }}
    >
      <DialogContent
        className="max-w-sm gap-0 overflow-hidden p-0 sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center px-6 pb-6 pt-8 text-center">
          <div className="relative mb-6 flex h-20 w-20 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-amber-400/25" />
            <span className="absolute inset-2 rounded-full bg-amber-50" />
            <AlertCircle className="relative h-10 w-10 text-amber-600" />
          </div>

          <DialogHeader className="space-y-2 text-center">
            <DialogTitle className="text-xl font-bold">
              Something went wrong
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              Your session has expired or could not be renewed. Please sign in
              again to continue.
            </DialogDescription>
          </DialogHeader>

          <Button
            onClick={handleOkay}
            className="mt-8 h-11 w-full bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
          >
            Okay
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
